/**
 * Loads a story's transcript `speaker_label` → `face_label`s map — the input
 * every active-speaker framing path needs — from its voice mappings and
 * face-identification data.
 */

import { fetchFaceDetectionsApi } from '@src/hooks/useFaceDetections';
import { err, ok, type Result } from '@src/services/common/Result';
import { buildSpeakerLabelToFaceLabelsMap } from '@src/services/framing/speakerRanking';
import { extractErrorInfo } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { fetchSpeakerVoiceMappings } from '@src/utility/speakerUtils';

import type { FaceDetectionsResponse } from '@src/services/framing/types';

const logger = LogManager.getLogger('service:loadSpeakerLabelToFaceLabels');

/**
 * Fails with `PRECONDITION` when a source is missing and `NOT_FOUND` when no
 * speaker resolves to a face, logging the counts so ops can see why
 * active-speaker framing didn't kick in.
 *
 * Pass `faceData` when the caller already fetched the story's face detections
 * to skip downloading them again.
 */
export async function loadSpeakerLabelToFaceLabels(
  storyId: string,
  faceData?: FaceDetectionsResponse | null,
): Promise<Result<Record<string, string[]>>> {
  try {
    const [voice, faces] = await Promise.all([
      fetchSpeakerVoiceMappings(storyId),
      faceData ?? fetchFaceDetectionsApi(storyId),
    ]);

    const voiceMappings = voice?.mappings;
    const identifications = faces?.speakerIdentifications;
    const faceTracks = faces?.faceTracks;
    const counts = {
      voiceCount: voiceMappings ? Object.keys(voiceMappings).length : 0,
      identificationsCount: identifications
        ? Object.keys(identifications).length
        : 0,
      faceTracksCount: faceTracks ? Object.keys(faceTracks).length : 0,
    };

    if (
      !voiceMappings ||
      !identifications ||
      !faceTracks ||
      counts.voiceCount === 0 ||
      counts.identificationsCount === 0 ||
      counts.faceTracksCount === 0
    ) {
      logger.info('Missing inputs for the speaker→face map', {
        storyId,
        ...counts,
      });
      return err(
        'PRECONDITION',
        'Needs voice mappings, face identifications and face tracks',
        counts,
      );
    }

    const map = buildSpeakerLabelToFaceLabelsMap(
      voiceMappings,
      identifications,
      faceTracks,
    );
    const resolvedCount = Object.keys(map).length;
    logger.info('Built the speaker→face map', {
      storyId,
      ...counts,
      resolvedCount,
    });

    return resolvedCount > 0
      ? ok(map)
      : err('NOT_FOUND', 'No transcript speaker resolves to a face', counts);
  } catch (error) {
    logger.warn('Failed to load the speaker→face map', {
      storyId,
      ...extractErrorInfo(error),
    });
    return err('EXTERNAL', 'Failed to load the speaker→face map', error);
  }
}
